import { useEffect, useRef, useState } from 'react'
import {
  getCachedAvatarDisplayUrl,
  invalidateAvatarDisplayUrl,
  resolveAvatarDisplayUrl,
} from '../services/profile-service'
import type { PresenceStatus } from '../types/chat'
import { hasPresenceDot, PRESENCE_DOT_CLASS } from '../utils/presence'
import { memberGreen, providerPurple, independentAmber } from '../utils/user-role-label'
import { BRAND_LOGO_URL } from '../utils/brand-logo'

export type AvatarVariant = 'member' | 'provider' | 'independent' | 'neutral'

type Props = {
  name: string
  avatarUrl?: string | null
  size?: number
  variant?: AvatarVariant
  className?: string
  /** When set, renders a colored presence dot on the avatar corner. */
  presence?: PresenceStatus | null
  /**
   * When set, renders this emoji as a corner glyph INSTEAD of the presence dot.
   * Used for custom activity statuses (e.g. 🌴 Out of office, 🏠 Working
   * remotely, 🎯 Focus time) so the icon conveys the status without redundant
   * text. A custom status replaces the plain active/idle dot.
   */
  statusEmoji?: string | null
  /**
   * What to show when there is no photo. 'initials' (default) for lists;
   * 'logo' shows the Ascend brand mark — used for the in-call avatar so a
   * photo-less peer reads as a branded call rather than a plain initials disc.
   */
  fallback?: 'initials' | 'logo'
}

const VARIANT_STYLES: Record<AvatarVariant, { bg: string; ring: string }> = {
  member: { bg: memberGreen.avatarBg, ring: memberGreen.avatarRing },
  provider: { bg: providerPurple.avatarBg, ring: providerPurple.avatarRing },
  independent: { bg: independentAmber.avatarBg, ring: independentAmber.avatarRing },
  neutral: { bg: 'bg-brand-600', ring: '' },
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function presenceDotSize(avatarSize: number): string {
  if (avatarSize >= 72) return 'h-3.5 w-3.5'
  if (avatarSize >= 40) return 'h-2.5 w-2.5'
  return 'h-2 w-2'
}

function statusGlyphSize(avatarSize: number): { box: string; text: string } {
  if (avatarSize >= 72) return { box: 'h-6 w-6', text: 'text-sm' }
  if (avatarSize >= 40) return { box: 'h-4 w-4', text: 'text-[11px]' }
  return { box: 'h-3.5 w-3.5', text: 'text-[10px]' }
}

export function AvatarImage({
  name,
  avatarUrl,
  size = 40,
  variant = 'neutral',
  className = '',
  presence = null,
  statusEmoji = null,
  fallback = 'initials',
}: Props) {
  // Seed from the synchronous cache so a remount (list scroll / navigation)
  // paints the photo immediately instead of flashing the fallback while an
  // effect re-resolves the URL. `undefined` from the cache means "not yet
  // resolved" → start as null (fallback) and fill in via the effect below.
  const [src, setSrc] = useState<string | null>(() => {
    const cached = getCachedAvatarDisplayUrl(avatarUrl)
    return cached === undefined ? null : cached
  })
  const retriedRef = useRef(false)
  const styles = VARIANT_STYLES[variant]

  const handleImageError = () => {
    // Dead URL (expired presign, flaky network): drop it from the cache so
    // future mounts re-resolve, try ONE fresh presign, then settle on the
    // initials/logo fallback — never the browser's broken-image chrome.
    invalidateAvatarDisplayUrl(avatarUrl)
    setSrc(null)
    if (retriedRef.current) return
    retriedRef.current = true
    void resolveAvatarDisplayUrl(avatarUrl).then((url) => {
      if (url) setSrc(url)
    })
  }

  useEffect(() => {
    let cancelled = false
    retriedRef.current = false
    const cached = getCachedAvatarDisplayUrl(avatarUrl)
    if (cached !== undefined) {
      // Resolved synchronously (no avatar, stable URL, or fresh cache hit).
      setSrc(cached)
      return
    }
    void resolveAvatarDisplayUrl(avatarUrl).then((url) => {
      if (!cancelled) setSrc(url)
    })
    return () => {
      cancelled = true
    }
  }, [avatarUrl])

  const dimensionStyle = { width: size, height: size }
  const ringClass = styles.ring
  // A custom activity status (emoji glyph) REPLACES the presence dot so we never
  // show both — the glyph alone conveys the status.
  const showStatusGlyph = Boolean(statusEmoji)
  // Offline shows NO indicator at all — only ACTIVE/IDLE render a dot.
  const showDot = !showStatusGlyph && hasPresenceDot(presence)
  const dotClass = presence ? PRESENCE_DOT_CLASS[presence] : ''
  const dotSizeClass = presenceDotSize(size)
  const glyphSize = statusGlyphSize(size)

  const avatarNode = src ? (
    <img
      src={src}
      alt={name}
      style={dimensionStyle}
      // A URL that fails to load (expired presign, flaky network mid-call)
      // must degrade to the initials/logo fallback, never broken-image chrome.
      onError={handleImageError}
      className={`shrink-0 rounded-full object-cover ${ringClass} ${className}`}
    />
  ) : fallback === 'logo' ? (
    <div
      style={dimensionStyle}
      className={`flex shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 ${ringClass} ${className}`}
    >
      <img
        src={BRAND_LOGO_URL}
        alt={name}
        className="h-3/5 w-3/5 object-contain opacity-95"
      />
    </div>
  ) : (
    <div
      style={dimensionStyle}
      className={`flex shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${styles.bg} ${ringClass} ${className}`}
    >
      {initials(name)}
    </div>
  )

  if (!showStatusGlyph && !showDot) return avatarNode

  return (
    // Explicit dimensions stop flex parents stretching the wrapper, which
    // would float the presence dot away from the avatar corner.
    <span className="relative inline-flex shrink-0 self-center" style={dimensionStyle}>
      {avatarNode}
      {showStatusGlyph ? (
        <span
          className={`absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full bg-white leading-none ring-2 ring-white ${glyphSize.box} ${glyphSize.text}`}
          aria-hidden
        >
          {statusEmoji}
        </span>
      ) : (
        <span
          className={`absolute bottom-0 right-0 block rounded-full ring-2 ring-white ${dotSizeClass} ${dotClass}`}
          aria-hidden
        />
      )}
    </span>
  )
}
